import React from 'react';
import './style.scss';
import PropTypes from "prop-types";
import completeIcon from "./complete.svg"
import currentIcon from "./current.svg"
import pauseIcon from "./pause.svg"
import returnIcon from "./return.svg"
import greyDot from "./greydot.svg"
import {FormattedMessage} from 'react-intl';

export default class OrderMilestoneTimeline extends React.Component {
    static propTypes = {
        trackingEvents: PropTypes.object.isRequired,
        serviceType: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        subStatus: PropTypes.string.isRequired,
        isReblast: PropTypes.bool,
        cancelReasonDescription: PropTypes.string
    };

    createMilestone = {
        status: "create",
        text: "GoSendPartner.Timeline.created",
        validSubStatus: ["confirmed", "allocated", "delivered", "on-hold", "out-for-delivery", "out-for-pickup", "rejected", "picked"],
    }

    allocateMilestone = {
        status: "allocate",
        text: "GoSendPartner.Timeline.allocated",
        validSubStatus: ["allocated", "delivered", "on-hold", "out-for-delivery", "out-for-pickup", "rejected", "picked"],
    }

    outForPickupMilestone = {
        status: "outForPickup",
        text: "GoSendPartner.Timeline.outForPickup",
        validSubStatus: ["delivered", "on-hold", "out-for-delivery", "out-for-pickup", "rejected", "picked"],
    }

    pickupMilestone = {
        status: "pickup",
        text: "GoSendPartner.Timeline.picked",
        validSubStatus: ["delivered", "on-hold", "out-for-delivery", "rejected", "picked"],
    }

    outForDeliveryMilestone = {
        status: "outForDelivery",
        text: "GoSendPartner.Timeline.outForDelivery",
        validSubStatus: ["delivered", "on-hold", "out-for-delivery", "rejected"],
    }

    onHoldMilestone = {
        status: "onHold",
        text: "GoSendPartner.Timeline.onHold",
        validSubStatus: ["delivered", "on-hold", "rejected"],
    }

    deliveryMilestone = {
        status: "delivery",
        text: "GoSendPartner.Timeline.delivered",
        validSubStatus: ["delivered"],
    }

    rejectedMilestone = {
        status: "reject",
        text: "GoSendPartner.Timeline.rejected",
        validSubStatus: ["rejected"],
    }

    sameDay = "sameday-kilat";
    instant = "go-kilat-new";

    trackingEventsSubStatusMap = {
        confirmed: "create",
        allocated: "allocate",
        delivered: "delivery",
        rejected: "reject",
        picked: "pickup",
        "on-hold": "onHold",
        "out-for-delivery": "outForDelivery",
        "out-for-pickup": "outForPickup",
    }

    getTimestamp(key) {
        const {trackingEvents} = this.props;
        if (trackingEvents[key]) {
            return this.formatTimestamp(trackingEvents[key]);
        }

        return null
    }

    getSubText(key) {
        if ((key === 'onHold' || key === 'reject') && this.props.cancelReasonDescription) {
            const reason_code = this.props.cancelReasonDescription.split('-')[0]
            return (
                <>
                    <FormattedMessage id="GoSendPartner.Reason"/>
                    <span>: </span>
                    <FormattedMessage id={`GoSendPartner.Timeline.CancellationReason.${reason_code}`} defaultMessage="Others"/>
                </>
            );
        }

        if(key === 'delivery' && this.props.receiverName) {
            return (
                <>
                    <FormattedMessage id="GoSendPartner.Receiver"/>
                    <span>: {this.props.receiverName}</span>
                </>
            );
        }

        return null
    }

    getActive(key) {
        const {trackingEvents, subStatus} = this.props;

        if (key === 'onHold' && subStatus === 'on-hold') {
            return true
        }

        if (trackingEvents[key]) {
            return true
        }

        return false
    }

    getIconType(key, isActive) {
        const {subStatus} = this.props;
        if (isActive) {
            if (key === 'onHold' && subStatus === 'on-hold') {
                return pauseIcon;
            } else if (key === 'reject') {
                return returnIcon;
            } else if (key === "delivery") {
                return completeIcon;
            } else if (key === this.trackingEventsSubStatusMap[subStatus]) {
                return currentIcon;
            } else {
                return completeIcon;
            }
        } else {
            return greyDot;
        }
    }

    createTimelineObject = (timelineObject) => {
        const {subStatus} = this.props;
        if (timelineObject.validSubStatus.includes(subStatus)) {
            const isActive = this.getActive(timelineObject.status);
            return {
                ...timelineObject,
                subtext: this.getSubText(timelineObject.status),
                timestamp: this.getTimestamp(timelineObject.status),
                active: isActive,
                iconType: this.getIconType(timelineObject.status, isActive)
            }
        }

        return timelineObject
    }

    buildOrderTimeline() {
        let timeline = [];
        if (this.props.serviceType === this.instant) {
            timeline = [
                this.createTimelineObject(this.createMilestone),
                this.createTimelineObject(this.outForPickupMilestone),
                this.createTimelineObject(this.outForDeliveryMilestone),
            ]
        } else {
            timeline = [
                this.createTimelineObject(this.createMilestone),
                this.createTimelineObject(this.allocateMilestone),
                this.createTimelineObject(this.outForPickupMilestone),
                this.createTimelineObject(this.pickupMilestone),
                this.createTimelineObject(this.outForDeliveryMilestone),
            ]
        }

        if (this.props.subStatus === "on-hold") {
            timeline.push(this.createTimelineObject(this.onHoldMilestone))
        }
        if (this.props.status === "rejected") {
            timeline.push(this.createTimelineObject(this.rejectedMilestone))
        } else {
            timeline.push(this.createTimelineObject(this.deliveryMilestone))
        }

        return timeline
    }

    formatTimestamp(timestamp) {
        if (timestamp !== null && timestamp !== "") {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) {
                // Handle the invalid date case
                return null;
            }
            return new Intl.DateTimeFormat('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(date);
        }
        return null;
    }

    render() {
        const milestones = this.buildOrderTimeline();
        return (
            <div className="OrderMilestone">
                <div className="Title">
                    <FormattedMessage id="GoSendPartner.OrderStatus"/>
                </div>
                {milestones.map((milestone, index) => (
                    <div className="MilestoneContainer" key={index}>
                        <div className="Milestone">
                            <div className="IconAndStatusColumn">
                                <div className="IconColumn">
                                    <div className="Icon"><img alt="icon" src={milestone.iconType || greyDot}/></div>
                                </div>
                                <div className={`TextColumn ${milestone.active && "active"}`}>
                                    <FormattedMessage id={milestone.text}/>
                                    <br/>
                                    {milestone.subtext !== null && (
                                        <div className="SubTextRow">{milestone.subtext}</div>)}
                                </div>
                            </div>
                            <div className="TimestampColumn">
                                {milestone.timestamp}
                            </div>
                        </div>
                        {index !== milestones.length - 1 && (<div
                            className={`VerticalSeparator ${milestone.active && milestone.iconType === completeIcon && "active"}`}/>)}
                    </div>
                ))}
            </div>
        );
    }
}

